import { Component } from '@theme/component'
import { morph } from '@theme/morph'
import { DialogOpenEvent } from '@theme/dialog'
import { StandardEvents } from '@shopify/events'
import {
  addLinesToCart,
  resolveCartErrorMessage,
  resolveFetchErrorMessage,
  isAbortError
} from '@theme/cart-add'
import { triggerFlyToCart } from '@theme/fly-to-cart'
import { THEME_BREAKPOINTS } from '@theme/utilities'

/**
 * Module-level cache shared across every instance of the component.
 * Keyed by request URL so the desktop pane and mobile carousel only
 * fetch once for a given seed product.
 *
 * @type {Map<string, Promise<string>>}
 */
const sharedFetchCache = new Map()

/** Clears module fetch cache (for tests only). */
export function clearCartRecommendationsFetchCache() {
  sharedFetchCache.clear()
}

/** @type {Promise<unknown> | null} */
let quickAddModulePromise = null

/**
 * Loads the quick-add modal as soon as a recommendation card needs it.
 * The theme loader also watches for the tag; starting the import here
 * covers the click that happens in the same turn the cards appear.
 */
function ensureQuickAddModule() {
  if (quickAddModulePromise) return quickAddModulePromise

  quickAddModulePromise = import('@theme/quick-add').catch((error) => {
    quickAddModulePromise = null
    if (window.Shopify?.designMode) {
      console.error('[theme] Quick add failed to load', error)
    }
  })

  return quickAddModulePromise
}

const SKELETON_DELAY_MS = 120
const RETRY_DELAY_MS = 250

/**
 * @param {object} params
 * @param {string} params.productId
 * @param {string} [params.url]
 * @param {string} [params.sectionId]
 * @param {string} [params.intent]
 * @param {string | number} [params.limit]
 * @param {string} [params.origin]
 * @returns {URL}
 */
export function buildRecommendationsUrl({
  productId,
  url,
  sectionId,
  intent,
  limit,
  origin = 'https://example.com'
}) {
  const recommendationPath = url || '/recommendations/products'
  const recommendationSectionId = sectionId || 'cart-recommendations'

  const requestUrl = new URL(recommendationPath, origin)
  requestUrl.searchParams.set('product_id', productId)
  requestUrl.searchParams.set('section_id', recommendationSectionId)

  const limitNum = Number(limit || '4')
  const fetchLimit = limitNum + 4
  requestUrl.searchParams.set('limit', fetchLimit.toString())

  if (intent) requestUrl.searchParams.set('intent', intent)

  return requestUrl
}

/**
 * @param {URL} currentUrl
 * @returns {URL | null}
 */
export function buildFallbackRecommendationsUrl(currentUrl) {
  const currentIntent = currentUrl.searchParams.get('intent') || 'complementary'
  const fallbackIntent =
    currentIntent === 'complementary' ? 'related' : 'complementary'
  const fallbackUrl = new URL(currentUrl)
  fallbackUrl.searchParams.set('intent', fallbackIntent)

  return fallbackUrl.toString() === currentUrl.toString() ? null : fallbackUrl
}

/**
 * @param {object} params
 * @param {string} [params.seedStrategy]
 * @param {string[]} params.productIds
 * @param {string} [params.latestProductId]
 * @param {string} [params.firstProductId]
 * @param {string} [params.highestPriceProductId]
 * @returns {string}
 */
export function resolveSeedProductId({
  seedStrategy,
  productIds,
  latestProductId,
  firstProductId,
  highestPriceProductId
}) {
  if (!productIds.length) return ''

  if (
    seedStrategy === 'latest_changed' &&
    latestProductId &&
    productIds.includes(latestProductId)
  ) {
    return latestProductId
  }

  if (seedStrategy === 'highest_price') {
    if (highestPriceProductId && productIds.includes(highestPriceProductId)) {
      return highestPriceProductId
    }
  }

  return firstProductId || productIds[0] || ''
}

/**
 * Reads the cart product id metadata from a section HTML payload by locating
 * the matching `<cart-recommendations-component>` (matched by data-mode) and
 * lifting its data-* attributes. Returns null when the payload is missing
 * the component (e.g. recommendations disabled at the section level).
 *
 * @param {string} sectionHtml
 * @param {string | undefined} mode
 * @returns {{
 *   cartProductIds: string,
 *   firstProductId: string,
 *   highestPriceProductId: string
 * } | null}
 */
export function parseCartRecommendationsState(sectionHtml, mode) {
  if (typeof sectionHtml !== 'string' || !sectionHtml) return null

  const doc = new DOMParser().parseFromString(sectionHtml, 'text/html')
  const selector = mode
    ? `cart-recommendations-component[data-mode="${mode}"]`
    : 'cart-recommendations-component'
  const node = doc.querySelector(selector)
  if (!node) return null

  return {
    cartProductIds: node.getAttribute('data-cart-product-ids') ?? '',
    firstProductId: node.getAttribute('data-first-product-id') ?? '',
    highestPriceProductId:
      node.getAttribute('data-highest-price-product-id') ?? ''
  }
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} content
 * @property {HTMLElement} liveRegion
 */

/**
 * Renders product recommendations inside the cart drawer.
 * Lives in two slots (desktop pane + mobile carousel) and shares
 * fetched HTML via {@link sharedFetchCache}.
 *
 * @extends {Component<Refs>}
 */
class CartRecommendationsComponent extends Component {
  requiredRefs = ['content', 'liveRegion']

  /** @type {AbortController | null} */
  #activeFetch = null

  /** @type {AbortController | null} */
  #cartDataController = null

  /** @type {Set<string>} */
  #viewedUrls = new Set()

  /** @type {string | null} */
  #latestProductId = null

  /** @type {Promise<void> | null} */
  #cartDataPromise = null

  /** @type {ReturnType<typeof setTimeout> | null} */
  #skeletonTimer = null

  /** @type {MediaQueryList | null} */
  #desktopMql = null

  /**
   * Monotonic counter used to invalidate stale fetch responses. Each call to
   * `#refresh` increments this, and `#render` rejects any HTML whose epoch
   * does not match the current one — defends against shared-cache races
   * where a fetch resolves after the cart has moved on.
   *
   * @type {number}
   */
  #renderEpoch = 0

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate
    )

    const drawer = this.closest('cart-drawer-component')
    drawer?.addEventListener(DialogOpenEvent.eventName, this.#refresh)

    this.addEventListener('click', this.#handleCardClick)
    this.addEventListener('submit', this.#handleQuickAddSubmit)

    // The desktop pane only widens the dialog at >=990px. When the viewport
    // crosses that boundary we need to re-sync the dialog grid class so a
    // tablet -> desktop resize does not leave the pane visible inside a
    // narrow dialog (or vice versa).
    if (
      this.dataset.mode === 'desktop' &&
      typeof window?.matchMedia === 'function'
    ) {
      this.#desktopMql = window.matchMedia(
        `(min-width: ${THEME_BREAKPOINTS.spacingMin}px)`
      )
      this.#desktopMql.addEventListener(
        'change',
        this.#handleDesktopBreakpointChange
      )
    }

    queueMicrotask(this.#refresh)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate
    )

    const drawer = this.closest('cart-drawer-component')
    drawer?.removeEventListener(DialogOpenEvent.eventName, this.#refresh)

    this.removeEventListener('click', this.#handleCardClick)
    this.removeEventListener('submit', this.#handleQuickAddSubmit)

    this.#desktopMql?.removeEventListener(
      'change',
      this.#handleDesktopBreakpointChange
    )
    this.#desktopMql = null

    this.abortPendingWork()
    this.#viewedUrls.clear()
    this.#latestProductId = null
  }

  /**
   * Cancels any in-flight fetch work without hiding the pane. Called by the
   * cart drawer on close so a slow response cannot resolve into a stale
   * pane after the user dismissed the drawer.
   */
  abortPendingWork() {
    this.#activeFetch?.abort()
    this.#activeFetch = null
    this.#cartDataController?.abort()
    this.#cartDataController = null
    this.#cartDataPromise = null
    this.#clearSkeletonTimer()
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  #handleDesktopBreakpointChange = (event) => {
    if (this.dataset.mode !== 'desktop') return
    const dialog = this.closest('.cart-drawer__dialog')
    if (!dialog) return

    const drawer = this.closest('cart-drawer-component')
    const drawerIsOpen =
      drawer instanceof HTMLElement &&
      drawer.dataset.dialogOpen === 'true' &&
      dialog instanceof HTMLDialogElement &&
      dialog.open

    if (!event.matches || !drawerIsOpen) {
      // Tablet/mobile: never widen the dialog. Strip the class unconditionally
      // so a desktop -> tablet resize cannot leave it behind.
      dialog.classList.remove('cart-drawer__dialog--has-recommendations')
      return
    }

    // Tablet -> desktop: re-add the class only if we currently have content,
    // otherwise the cart drawer should keep its single-card layout.
    const hasContent =
      !this.hidden &&
      Boolean(
        this.refs.content?.querySelector?.('[data-cart-recommendation-card]')
      )
    dialog.classList.toggle(
      'cart-drawer__dialog--has-recommendations',
      hasContent
    )
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    const processDetail = (detail) => {
      if (detail?.didError) return

      const productId = detail?.productId
      const itemCount = detail?.itemCount
      const sections = detail?.sections
      const sourceSectionId = this.dataset.sourceSectionId
      const sectionHtml =
        sections && sourceSectionId ? sections[sourceSectionId] : null

      if (productId) {
        this.#latestProductId = productId.toString()
      }

      sharedFetchCache.clear()

      this.#activeFetch?.abort()
      this.#activeFetch = null
      this.#clearSkeletonTimer()

      const parsed = sectionHtml
        ? parseCartRecommendationsState(sectionHtml, this.dataset.mode)
        : null

      if (parsed) {
        this.dataset.cartProductIds = parsed.cartProductIds
        this.dataset.firstProductId = parsed.firstProductId
        this.dataset.highestPriceProductId = parsed.highestPriceProductId

        if (itemCount === 0 || !parsed.cartProductIds) {
          this.#renderEpoch += 1
          this.#hide()
          return
        }
      } else {
        delete this.dataset.cartProductIds
        delete this.dataset.firstProductId
        delete this.dataset.highestPriceProductId

        if (itemCount === 0) {
          this.#renderEpoch += 1
          this.#hide()
          return
        }
      }

      queueMicrotask(this.#refresh)
    }

    if (event.promise) {
      event.promise
        .then(({ detail }) => processDetail(detail))
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn(
              '[cart-recommendations] Event promise rejected:',
              error
            )
          }
        })
      return
    }

    const legacyDetail = /** @type {CustomEvent} */ (event).detail?.data
    if (legacyDetail) processDetail(legacyDetail)
  }

  #refresh = async () => {
    if (!this.isConnected) return

    this.#renderEpoch += 1
    const epoch = this.#renderEpoch

    const productId = await this.#getSeedProductId()

    if (!this.isConnected || epoch !== this.#renderEpoch) return

    if (!productId) {
      this.#hide()
      return
    }

    const url = this.#buildUrl(productId)

    this.#scheduleSkeleton()

    try {
      const html = await this.#fetchRecommendationsWithRetry(url)
      if (epoch !== this.#renderEpoch || !this.isConnected) return
      if (this.#render(html, url, epoch)) return

      const fallbackUrl = this.#buildFallbackUrl(url)

      if (fallbackUrl) {
        const fallbackHtml =
          await this.#fetchRecommendationsWithRetry(fallbackUrl)
        if (epoch !== this.#renderEpoch || !this.isConnected) return
        if (this.#render(fallbackHtml, fallbackUrl, epoch)) return
      }

      this.#hide()
    } catch (error) {
      const err = /** @type {Error & { name?: string }} */ (error)
      if (err?.name === 'AbortError') return
      if (epoch !== this.#renderEpoch || !this.isConnected) return

      this.#handleError(err)
    } finally {
      if (epoch === this.#renderEpoch) {
        this.#clearSkeletonTimer()
        this.setAttribute('aria-busy', 'false')
        this.refs.content?.setAttribute('aria-busy', 'false')
      }
    }
  }

  /**
   * @returns {Promise<string>}
   */
  async #getSeedProductId() {
    const { seedStrategy } = this.dataset
    let productIds = this.#getCartProductIds()

    if (!productIds.length) {
      await this.#loadCartData()
      productIds = this.#getCartProductIds()
    }

    if (!productIds.length) return ''

    return resolveSeedProductId({
      seedStrategy,
      productIds,
      latestProductId: this.#latestProductId || undefined,
      firstProductId: this.dataset.firstProductId,
      highestPriceProductId: this.dataset.highestPriceProductId
    })
  }

  /**
   * @returns {string[]}
   */
  #getCartProductIds() {
    const { cartProductIds } = this.dataset
    if (!cartProductIds) return []
    return cartProductIds.split(',').filter(Boolean)
  }

  async #loadCartData() {
    if (this.#cartDataPromise) {
      await this.#cartDataPromise
      return
    }

    this.#cartDataController = new AbortController()
    const signal = this.#cartDataController.signal

    this.#cartDataPromise = fetch('/cart.js', { signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart returned ${response.status}`)
        return response.json()
      })
      .then((cart) => {
        if (!this.isConnected) return
        const items = Array.isArray(cart?.items) ? cart.items : []
        const productIds = items
          .map((item) => item.product_id?.toString())
          .filter(Boolean)

        this.dataset.cartProductIds = productIds.join(',')
        this.dataset.firstProductId = productIds[0] || ''

        const highestPriceItem = items.reduce((highest, item) => {
          if (!highest) return item
          return (item.final_line_price || 0) > (highest.final_line_price || 0)
            ? item
            : highest
        }, null)

        this.dataset.highestPriceProductId =
          highestPriceItem?.product_id?.toString() || ''
      })
      .catch(() => {
        // Surfaced via the regular hide() path; do not bubble.
      })
      .finally(() => {
        this.#cartDataPromise = null
        this.#cartDataController = null
      })

    await this.#cartDataPromise
  }

  /**
   * @param {string} productId
   * @returns {URL}
   */
  #buildUrl(productId) {
    const { url, sectionId, intent, limit } = this.dataset

    return buildRecommendationsUrl({
      productId,
      url,
      sectionId,
      intent,
      limit,
      origin: window.location.origin
    })
  }

  /**
   * @param {URL} currentUrl
   * @returns {URL | null}
   */
  #buildFallbackUrl(currentUrl) {
    return buildFallbackRecommendationsUrl(currentUrl)
  }

  /**
   * Wraps `#fetchRecommendations` with a single retry on transient failure.
   * Network errors and 5xx responses retry once after a short delay; aborts
   * and 4xx responses propagate immediately.
   *
   * @param {URL} url
   * @returns {Promise<string>}
   */
  async #fetchRecommendationsWithRetry(url) {
    try {
      return await this.#fetchRecommendations(url)
    } catch (error) {
      const err = /** @type {Error & { name?: string, status?: number }} */ (
        error
      )
      if (err?.name === 'AbortError') throw err

      const transient = !err?.status || (err.status >= 500 && err.status < 600)
      if (!transient) throw err

      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
      if (!this.isConnected) throw new DOMException('Aborted', 'AbortError')

      return this.#fetchRecommendations(url)
    }
  }

  /**
   * @param {URL} url
   * @returns {Promise<string>}
   */
  async #fetchRecommendations(url) {
    const cacheKey = url.toString()

    const cachedPromise = sharedFetchCache.get(cacheKey)
    if (cachedPromise) return cachedPromise

    this.#activeFetch?.abort()
    this.#activeFetch = new AbortController()
    const signal = this.#activeFetch.signal

    const promise = fetch(url, { signal })
      .then((response) => {
        if (!response.ok) {
          const error = /** @type {Error & { status?: number }} */ (
            new Error(`Server returned ${response.status}`)
          )
          error.status = response.status
          throw error
        }
        return response.text()
      })
      .catch((error) => {
        // Don't pollute the cache on failure
        sharedFetchCache.delete(cacheKey)
        throw error
      })
      .finally(() => {
        if (this.#activeFetch?.signal === signal) {
          this.#activeFetch = null
        }
      })

    sharedFetchCache.set(cacheKey, promise)
    return promise
  }

  /**
   * @param {string} html
   * @param {URL} url
   * @param {number} epoch
   * @returns {boolean}
   */
  #render(html, url, epoch) {
    if (epoch !== this.#renderEpoch || !this.isConnected) return false

    const doc = new DOMParser().parseFromString(html, 'text/html')
    const section = doc.querySelector('[data-cart-recommendations-section]')
    const count = Number(
      doc.querySelector('[data-cart-recommendations-count]')?.textContent || '0'
    )

    if (!section || count <= 0) return false

    const incoming = /** @type {HTMLElement} */ (section.cloneNode(true))

    if (incoming.querySelector('quick-add-component')) {
      ensureQuickAddModule()
    }

    morph(this.refs.content, incoming)
    this.hidden = false
    this.#setDrawerExpanded(true)
    this.refs.liveRegion.textContent = this.dataset.loadedText || ''
    this.#trackView(url)

    return true
  }

  #scheduleSkeleton() {
    this.setAttribute('aria-busy', 'true')
    this.refs.content?.setAttribute('aria-busy', 'true')
    this.refs.liveRegion.textContent = this.dataset.loadingText || ''

    const epoch = this.#renderEpoch

    // Avoid the flicker for cached responses by delaying the skeleton.
    this.#clearSkeletonTimer()
    this.#skeletonTimer = setTimeout(() => {
      if (!this.isConnected) return

      // Only show the skeleton if no content has been rendered yet.
      if (this.refs.content.querySelector('[data-cart-recommendation-card]')) {
        return
      }

      if (epoch !== this.#renderEpoch || !this.#getCartProductIds().length) {
        return
      }

      const dialog = this.closest('.cart-drawer__dialog')
      const drawer = this.closest('cart-drawer-component')
      if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return
      if (!drawer?.dataset?.dialogOpen) return

      this.hidden = false
      this.#setDrawerExpanded(true)

      const skeleton = document.createElement('div')
      skeleton.className = 'cart-recommendations__skeleton'
      skeleton.setAttribute('data-cart-recommendations-skeleton', '')

      this.refs.content.replaceChildren(
        skeleton.cloneNode(),
        skeleton.cloneNode(),
        skeleton.cloneNode()
      )
    }, SKELETON_DELAY_MS)
  }

  #clearSkeletonTimer() {
    if (this.#skeletonTimer != null) {
      clearTimeout(this.#skeletonTimer)
      this.#skeletonTimer = null
    }
  }

  #hide() {
    this.#clearSkeletonTimer()
    this.hidden = true
    this.#setDrawerExpanded(false)
    this.refs.content.replaceChildren()
    this.refs.content.setAttribute('aria-busy', 'false')
    this.setAttribute('aria-busy', 'false')
    // Clear the live region so screen readers don't announce stale loading
    // copy after the pane has been hidden.
    if (this.refs.liveRegion) this.refs.liveRegion.textContent = ''
  }

  /**
   * @param {Error} error
   */
  #handleError(error) {
    if (window.Shopify?.designMode) {
      console.error('[theme] Cart recommendations error', error)
    }
    this.#hide()
  }

  /**
   * Toggles the dialog grid layout. Only the desktop instance owns this class
   * so the dialog never widens on mobile or tablet (< 990px), where the
   * in-drawer horizontal carousel takes over.
   *
   * Removal must always be allowed (cleanup path), even at narrow widths, in
   * case a viewport resize from desktop -> tablet leaves the class behind.
   *
   * @param {boolean} expanded
   */
  #setDrawerExpanded(expanded) {
    if (this.dataset.mode !== 'desktop') return

    const dialog = this.closest('.cart-drawer__dialog')
    if (!dialog) return

    const drawer = this.closest('cart-drawer-component')

    if (expanded) {
      if (!this.#isDesktopViewport()) return
      if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return
      if (!drawer?.dataset?.dialogOpen) return
    }

    dialog.classList.toggle(
      'cart-drawer__dialog--has-recommendations',
      expanded
    )
  }

  /**
   * @returns {boolean}
   */
  #isDesktopViewport() {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return true
    }
    return window.matchMedia(`(min-width: ${THEME_BREAKPOINTS.spacingMin}px)`)
      .matches
  }

  /**
   * @param {URL} url
   */
  #trackView(url) {
    const cacheKey = url.toString()
    if (this.#viewedUrls.has(cacheKey)) return
    this.#viewedUrls.add(cacheKey)

    this.#pushAnalytics(() => {
      const isPageMode = this.dataset.mode === 'page'

      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          item_list_id: isPageMode
            ? 'cart_page_recommendations'
            : 'cart_drawer_recommendations',
          item_list_name: isPageMode
            ? 'Cart page recommendations'
            : 'Cart drawer recommendations',
          items: this.#getAnalyticsItems()
        }
      })
    })
  }

  /**
   * @param {() => void} fn
   */
  #pushAnalytics(fn) {
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') {
      return
    }
    try {
      fn()
    } catch (error) {
      // Third-party tag managers occasionally wrap dataLayer.push and throw;
      // never let analytics break the cart UX.
      if (window.Shopify?.designMode) {
        console.error('[theme] Cart recommendations analytics error', error)
      }
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleCardClick = (event) => {
    const target = /** @type {Element | null} */ (event.target)
    const card = target?.closest?.('[data-cart-recommendation-card]')
    if (!card) return

    this.#pushAnalytics(() => {
      const isPageMode = this.dataset.mode === 'page'

      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          item_list_id: isPageMode
            ? 'cart_page_recommendations'
            : 'cart_drawer_recommendations',
          item_list_name: isPageMode
            ? 'Cart page recommendations'
            : 'Cart drawer recommendations',
          items: [
            {
              item_id: card.getAttribute('data-product-id') || '',
              item_name: card.getAttribute('data-product-title') || '',
              price: card.getAttribute('data-product-price') || ''
            }
          ]
        }
      })
    })
  }

  /**
   * Quick-add handler. Submits the form via fetch and dispatches CartAddEvent
   * so the cart drawer / cart bubble update without a full page reload.
   *
   * @param {SubmitEvent} event
   */
  #handleQuickAddSubmit = async (event) => {
    const target = /** @type {Element | null} */ (event.target)
    const form = target?.closest?.(
      'form[data-cart-recommendation-quick-add-form]'
    )
    if (!(form instanceof HTMLFormElement)) return

    event.preventDefault()

    const card = form.closest('[data-cart-recommendation-card]')
    const productId = card?.getAttribute('data-product-id') || ''
    const button = /** @type {HTMLButtonElement | null} */ (
      form.querySelector('[data-cart-recommendation-quick-add-button]')
    )
    const errorEl = card?.querySelector(
      '[data-cart-recommendation-quick-add-error]'
    )

    if (button?.disabled) return
    if (button) button.disabled = true
    this.#clearQuickAddError(button, errorEl)

    const formData = new FormData(form)
    const quantity = Number(formData.get('quantity')) || 1
    const variantId = formData.get('id')?.toString() || ''
    const sourceId = `cart-recommendation-quick-add-${productId || 'unknown'}`

    if (!variantId) {
      this.#presentQuickAddError(button, errorEl, resolveFetchErrorMessage())
      if (button) button.disabled = false
      return
    }

    const mediaImg = card?.querySelector('.cart-recommendation-card__media img')
    const imageUrl =
      mediaImg instanceof HTMLImageElement
        ? mediaImg.currentSrc || mediaImg.src
        : undefined

    triggerFlyToCart({ source: button, imageUrl })

    const result = await addLinesToCart({
      items: [{ id: variantId, quantity }],
      source: 'cart-recommendations',
      sourceId,
      productId,
      initiator: this
    })

    try {
      if (!result.ok) {
        const message =
          resolveCartErrorMessage(result.data) ||
          (result.error && !isAbortError(result.error)
            ? resolveFetchErrorMessage()
            : 'Add to cart failed')

        this.#presentQuickAddError(button, errorEl, message)

        if (result.error && !isAbortError(result.error)) {
          console.error('[cart-recommendations] Quick add failed', result.error)
        }
        return
      }

      this.#pushAnalytics(() => {
        const isPageMode = this.dataset.mode === 'page'

        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            item_list_id: isPageMode
              ? 'cart_page_recommendations'
              : 'cart_drawer_recommendations',
            item_list_name: isPageMode
              ? 'Cart page recommendations'
              : 'Cart drawer recommendations',
            items: [
              {
                item_id: productId,
                item_name: card?.getAttribute('data-product-title') || '',
                price: card?.getAttribute('data-product-price') || '',
                quantity
              }
            ]
          }
        })
      })
    } finally {
      if (button && button.isConnected) button.disabled = false
    }
  }

  /**
   * @param {HTMLButtonElement | null} button
   * @param {Element | null | undefined} errorEl
   */
  #clearQuickAddError(button, errorEl) {
    if (errorEl instanceof HTMLElement) {
      errorEl.hidden = true
      errorEl.classList.add('hidden')
      errorEl.textContent = ''
    }

    if (button instanceof HTMLButtonElement) {
      button.removeAttribute('aria-describedby')
      button.removeAttribute('aria-invalid')
    }
  }

  /**
   * Shows the card error under the title and points the button at it.
   * Desktop and mobile panes both render the same product, so the id
   * includes the pane mode.
   *
   * @param {HTMLButtonElement | null} button
   * @param {Element | null | undefined} errorEl
   * @param {string} message
   */
  #presentQuickAddError(button, errorEl, message) {
    if (!(errorEl instanceof HTMLElement)) return

    const card = errorEl.closest('[data-cart-recommendation-card]')
    const productId = card?.getAttribute('data-product-id') || 'item'
    const mode = this.dataset.mode || 'default'
    errorEl.id = `CartRecommendationError-${mode}-${productId}`
    errorEl.textContent = message
    errorEl.hidden = false
    errorEl.classList.remove('hidden')

    if (button instanceof HTMLButtonElement) {
      button.setAttribute('aria-describedby', errorEl.id)
      button.setAttribute('aria-invalid', 'true')
    }
  }

  /**
   * @returns {{ item_id: string, item_name: string, price: string }[]}
   */
  #getAnalyticsItems() {
    return [...this.querySelectorAll('[data-cart-recommendation-card]')].map(
      (card) => ({
        item_id: card.getAttribute('data-product-id') || '',
        item_name: card.getAttribute('data-product-title') || '',
        price: card.getAttribute('data-product-price') || ''
      })
    )
  }
}

if (!customElements.get('cart-recommendations-component')) {
  customElements.define(
    'cart-recommendations-component',
    CartRecommendationsComponent
  )
}
